<template>
  <div>
    <!-- Summary Cards -->
    <div class="row" v-if="pagination">
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-aqua">
          <div class="inner">
            <h3>{{ pagination.totalCount }}</h3>
            <p>Total Assignments</p>
          </div>
          <div class="icon">
            <i class="fa fa-file-text"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-green">
          <div class="inner">
            <h3>{{ pagination.currentPage }}</h3>
            <p>Current Page</p>
          </div>
          <div class="icon">
            <i class="fa fa-list"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-yellow">
          <div class="inner">
            <h3>{{ pagination.totalPages }}</h3>
            <p>Total Pages</p>
          </div>
          <div class="icon">
            <i class="fa fa-book"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-red">
          <div class="inner">
            <h3>{{ pagination.pageSize }}</h3>
            <p>Records per Page</p>
          </div>
          <div class="icon">
            <i class="fa fa-table"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
    </div>

    <!-- Pagination Controls -->
    <div class="row" v-if="pagination && pagination.totalCount > 0">
      <div class="col-md-12">
        <div class="box box-solid">
          <div class="box-body">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <label class="mr-2 mb-0">Show:</label>
                <b-form-select
                  v-model="perPage"
                  :options="pageOptions"
                  @change="handlePerPageChange"
                  size="sm"
                  class="w-auto"
                ></b-form-select>
                <span class="ml-2 text-muted">entries</span>
              </div>

              <b-pagination
                v-model="currentPage"
                :total-rows="pagination.totalCount"
                :per-page="perPage"
                @change="changePage"
                size="sm"
                class="mb-0"
                first-number
                last-number
              ></b-pagination>

              <div class="text-muted small">
                Showing {{ records.length ? ((currentPage - 1) * perPage + 1) : 0 }} to {{ Math.min(((currentPage - 1) * perPage) + records.length, pagination.totalCount || 0) }} of {{ pagination.totalCount || 0 }} entries
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="box box-default">
      <div class="box-header with-border">
        <h3 class="box-title">Assignment Details with Profit Loss</h3>
        <div class="box-tools pull-right">
          <button type="button" class="btn btn-success btn-sm" @click="exportToExcel">
            <i class="fa fa-download"></i> Export Excel
          </button>
        </div>
      </div>
      <div class="box-body">
        <div class="table-responsive">
          <table class="table table-bordered table-striped table-hover">
            <thead>
              <tr>
                <th>SN</th>
                <th>Assignment ID</th>
                <th>Job Series</th>
                <th>Job No</th>
                <th>Job Type</th>
                <th>Transport Type</th>
                <th>Client</th>
                <th>POR</th>
                <th>POL</th>
                <th>POD</th>
                <th>Branch</th>
                <th>Raised By</th>
                <th>Shipping Line</th>
                <th>Expenses</th>
                <th>Incomes</th>
                <th>Profit</th>
                <th>Profit %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(record, index) in records" :key="record.assignmentId"
                  :class="getRowClass(record)">
                <td>{{ ((currentPage - 1) * perPage) + index + 1 }}</td>
                <td>{{ record.assignmentId || 'N/A' }}</td>
                <td>{{ record.jobSeries }}</td>
                <td>{{ record.jobNo }}</td>
                <td>{{ record.jobType }}</td>
                <td>{{ record.transportType }}</td>
                <td>{{ record.client }}</td>
                <td>{{ record.por }}</td>
                <td>{{ record.pol }}</td>
                <td>{{ record.pod }}</td>
                <td>{{ record.branch }}</td>
                <td>{{ record.RaisedBy }}</td>
                <td>{{ record.shippingLine }}</td>
                <td class="text-right">{{ formatCurrency(record.expenses) }}</td>
                <td class="text-right">{{ formatCurrency(record.incomes) }}</td>
                <td class="text-right" :class="getProfitClass(record.profit)">
                  {{ formatCurrency(record.profit) }}
                </td>
                <td class="text-right" :class="getPercentageClass(record.profitPercentage)">
                  {{ record.profitPercentage }}%
                </td>
                <td>
                  <span class="label" :class="getStatusClass(record.status)">
                    {{ record.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- No Data Message -->
        <div v-if="records.length === 0" class="text-center text-muted">
          <i class="fa fa-info-circle fa-2x"></i>
          <p>No assignment data found for the selected filters.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GrowthDashboardService from '../../../services/GrowthDashboardService';
import * as XLSX from 'xlsx';

export default {
  name: 'AssignmentsGrowthComponent',
  props: {
    filters: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      records: [],
      pagination: null,
      currentPage: 1,
      perPage: 25,
      pageOptions: [25, 50, 100, 200]
    };
  },
  watch: {
    filters: {
      handler() {
        this.currentPage = 1;
        this.loadData(1);
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    notifyUser({ type = 'info', title = '', text = '' } = {}) {
      if (this.$bvToast) {
        const variantMap = { info: 'info', success: 'success', warning: 'warning', error: 'danger' };
        this.$bvToast.toast(text, {
          title: title || 'Info',
          variant: variantMap[type] || 'info',
          solid: true
        });
        return;
      }
      if (this.$notify) {
        this.$notify({ type, title: title || 'Info', text });
        return;
      }
      if (type === 'error') console.error(title, text);
      else console.log(title, text);
    },
    async fetchAllRecordsForExport() {
      const baseParams = Object.assign({}, this.filters);
      Object.keys(baseParams).forEach(key => {
        if (baseParams[key] === null || baseParams[key] === '') {
          delete baseParams[key];
        }
      });

      const exportPageSize = 500;
      const all = [];

      const firstRes = await GrowthDashboardService.getAssignmentJobDetails(
        Object.assign({}, baseParams, {
          page: 1,
          pageSize: exportPageSize
        })
      );

      if (!firstRes.data || !firstRes.data.success) return [];

      const firstData = firstRes.data.data || {};
      const firstRecords = Array.isArray(firstData.records) ? firstData.records : [];
      all.push(...firstRecords);

      const pagination = firstData.pagination || {};
      const totalPages = Number(pagination.totalPages || 1);

      for (let page = 2; page <= totalPages; page++) {
        const res = await GrowthDashboardService.getAssignmentJobDetails(
          Object.assign({}, baseParams, {
            page,
            pageSize: exportPageSize
          })
        );
        if (!res.data || !res.data.success) break;
        const data = res.data.data || {};
        const records = Array.isArray(data.records) ? data.records : [];
        all.push(...records);
      }

      return all;
    },
    async loadData(page = 1) {
      try {
        this.$emit('loading', true);

        const params = Object.assign({
          page: page,
          pageSize: this.perPage
        }, this.filters);

        // Remove null/empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === '') {
            delete params[key];
          }
        });

        const response = await GrowthDashboardService.getAssignmentJobDetails(params);

        if (response.data.success) {
          const responseData = response.data.data || {};
          this.records = Array.isArray(responseData.records) ? responseData.records : [];
          const serverPagination = responseData.pagination || {};
          this.pagination = {
            currentPage: Number(serverPagination.currentPage || page || 1),
            totalPages: Number(serverPagination.totalPages || 1),
            totalCount: Number(serverPagination.totalCount || this.records.length || 0),
            pageSize: Number(serverPagination.pageSize || this.perPage)
          };
          this.currentPage = this.pagination.currentPage;
        }
      } catch (error) {
        console.error('Error loading assignments data:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to load assignments data'
        });
      } finally {
        this.$emit('loading', false);
      }
    },

    changePage(page) {
      if (this.pagination && page >= 1 && page <= this.pagination.totalPages) {
        this.loadData(page);
      }
    },
    handlePerPageChange() {
      this.currentPage = 1;
      this.loadData(1);
    },

    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '₹0';
      return '₹' + Math.abs(amount).toLocaleString('en-IN');
    },

    getRowClass(record) {
      if (record.status === 'Loss') return 'danger';
      if (record.status === 'Profit') return 'success';
      return '';
    },

    getProfitClass(amount) {
      return amount >= 0 ? 'text-success' : 'text-danger';
    },

    getPercentageClass(percentage) {
      if (percentage > 0) return 'text-success';
      if (percentage < 0) return 'text-danger';
      return 'text-muted';
    },

    getStatusClass(status) {
      switch (status) {
        case 'Profit': return 'label-success';
        case 'Loss': return 'label-danger';
        default: return 'label-default';
      }
    },

    async exportToExcel() {
      try {
        this.notifyUser({
          type: 'info',
          title: 'Export',
          text: 'Preparing Excel export. Please wait...'
        });

        const allRecords = await this.fetchAllRecordsForExport();
        if (!allRecords || allRecords.length === 0) {
          this.notifyUser({
            type: 'warning',
            title: 'Export',
            text: 'No assignment data available to export.'
          });
          return;
        }

        const excelRows = allRecords.map((record, index) => ({
          SN: index + 1,
          'Assignment ID': record.assignmentId || 'N/A',
          'Job Series': record.jobSeries || '',
          'Job No': record.jobNo || '',
          'Job Type': record.jobType || '',
          'Transport Type': record.transportType || '',
          Client: record.client || '',
          POR: record.por || '',
          POL: record.pol || '',
          POD: record.pod || '',
          Branch: record.branch || '',
          'Raised By': record.RaisedBy || '',
          'Shipping Line': record.shippingLine || '',
          Expenses: record.expenses || 0,
          Incomes: record.incomes || 0,
          Profit: record.profit || 0,
          'Profit %': record.profitPercentage || 0,
          Status: record.status || ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Assignments');

        const timestamp = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `Assignments_Growth_${timestamp}.xlsx`);
      } catch (error) {
        console.error('Error exporting assignments data:', error);
        this.notifyUser({
          type: 'error',
          title: 'Error',
          text: 'Failed to export assignments data'
        });
      }
    }
  },

  mounted() {
    this.loadData();
  }
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.margin-lr-10 {
  margin: 0 10px;
}

.label {
  display: inline-block;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 4px;
}

.label-success {
  background-color: #5cb85c;
}

.label-danger {
  background-color: #d9534f;
}

.label-default {
  background-color: #999;
}

.small-box {
  margin-bottom: 15px;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.text-success {
  color: #3c763d !important;
}

.text-danger {
  color: #a94442 !important;
}

.text-muted {
  color: #999 !important;
}

.box-solid {
  border: 1px solid #ddd;
}

.box-solid > .box-header {
  background: #f5f5f5;
  background-color: #f5f5f5;
}

.box-solid > .box-header .box-title {
  color: #333;
}
</style>



// WEBPACK FOOTER //
// AssignmentsGrowthComponent.vue?1ac91bb3